import React, { useState, useEffect, useRef } from 'react';
import { createApiClient, setLogoutCallback, getFreshIdToken } from '@/services/api';
import type { AuthUser } from '@/services/cognito-auth';
import type { Message, ChatSession as ChatSessionType, ToolBlock } from '@/types';
import { LoadingDots } from './Loading';
import { ToolCallVisualization } from './ToolCallVisualization';
import { ChartRenderer } from './ChartRenderer';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

interface ChatProps {
  user: AuthUser;
  onSignOut: () => void;
}

export const Chat: React.FC<ChatProps> = ({ user, onSignOut }) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [currentSessionId, setCurrentSessionId] = useState<string | null>(null);
  const [sessions, setSessions] = useState<ChatSessionType[]>([]);
  const [showSidebar, setShowSidebar] = useState(true);
  const [editingMessageId, setEditingMessageId] = useState<string | null>(null);
  const [editContent, setEditContent] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const isAdmin = user.groups?.includes('asai_dev') || false;
  const userInfo = {
    email: user.email || user.username,
    name: user.name || user.email || user.username
  };

  // Set up logout callback on mount
  useEffect(() => {
    setLogoutCallback(onSignOut);
  }, [onSignOut]);

  // Load user sessions on mount
  useEffect(() => {
    loadSessions();
  }, []);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const loadSessions = async () => {
    try {
      const client = createApiClient();
      const response = await client.get('/chat/sessions');
      const sessions = response.data.sessions || [];
      // Sort sessions by created_at in descending order (most recent first)
      sessions.sort((a: ChatSessionType, b: ChatSessionType) =>
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      );
      setSessions(sessions);
    } catch (error) {
      console.error('Failed to load sessions:', error);
    }
  };

  const loadSessionHistory = async (sessionId: string) => {
    try {
      const client = createApiClient();
      const response = await client.get(`/chat/history/${sessionId}`);
      const loadedMessages: Message[] = response.data.messages.map((msg: any) => ({
        id: Math.random().toString(),
        role: msg.role as 'user' | 'assistant',
        content: msg.content,
        timestamp: new Date(msg.timestamp),
        message_id: msg.message_id,
        edited: msg.edited,
        edited_at: msg.edited_at,
        toolBlocks: msg.tool_blocks?.map((block: any) => ({
          tool: {
            id: block.tool.id,
            name: block.tool.name,
            input: block.tool.input,
          },
          result: block.result,
          timestamp: new Date(block.timestamp),
        })) || [],
      }));
      setMessages(loadedMessages);
      setCurrentSessionId(sessionId);
    } catch (error) {
      console.error('Failed to load session history:', error);
    }
  };

  const handleSendMessage = async () => {
    if (!inputMessage.trim() || isLoading) return;

    const userMessage: Message = {
      id: Math.random().toString(),
      role: 'user',
      content: inputMessage,
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    const currentMessage = inputMessage;
    setInputMessage('');
    setIsLoading(true);

    // Create placeholder for assistant message
    const assistantMessageId = Math.random().toString();
    const assistantMessage: Message = {
      id: assistantMessageId,
      role: 'assistant',
      content: '',
      timestamp: new Date(),
    };
    setMessages((prev) => [...prev, assistantMessage]);

    try {
      const idToken = await getFreshIdToken();
      if (!idToken) {
        throw new Error('Not authenticated');
      }

      const API_URL = import.meta.env.VITE_API_URL;

      // Use fetch for streaming
      const response = await fetch(`${API_URL}/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`,
        },
        body: JSON.stringify({
          message: currentMessage,
          session_id: currentSessionId || undefined,
          stream: true,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Stream the response in real-time
      const reader = response.body?.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let fullContent = '';
      const toolBlocks: ToolBlock[] = [];

      if (reader) {
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() || '';

          for (const line of lines) {
            if (line.startsWith('data: ')) {
              const data = line.slice(6);
              try {
                const event = JSON.parse(data);

                if (event.type === 'content_block_delta' && event.delta?.text) {
                  fullContent += event.delta.text;
                  // Update the assistant message in real-time
                  setMessages((prev) =>
                    prev.map((msg) =>
                      msg.id === assistantMessageId
                        ? { ...msg, content: fullContent, toolBlocks: [...toolBlocks] }
                        : msg
                    )
                  );
                } else if (event.type === 'tool_use_start') {
                  // Add tool block when tool starts
                  console.log('Tool use start:', event);
                  const toolBlock: ToolBlock = {
                    type: 'tool_use',
                    tool: {
                      id: event.tool_use.id,
                      name: event.tool_use.name,
                      input: event.tool_use.input, // May be undefined initially
                    },
                    timestamp: new Date(),
                  };
                  toolBlocks.push(toolBlock);

                  // Update message with tool block
                  setMessages((prev) =>
                    prev.map((msg) =>
                      msg.id === assistantMessageId
                        ? { ...msg, toolBlocks: [...toolBlocks] }
                        : msg
                    )
                  );
                } else if (event.type === 'tool_use_complete') {
                  // Update the tool block with complete input
                  console.log('Tool use complete:', event);
                  const toolIndex = toolBlocks.findIndex(tb => tb.tool.id === event.tool_use.id);
                  console.log('Tool index found:', toolIndex, 'Input:', event.tool_use.input);
                  if (toolIndex !== -1) {
                    toolBlocks[toolIndex].tool.input = event.tool_use.input;

                    // Update message
                    setMessages((prev) =>
                      prev.map((msg) =>
                        msg.id === assistantMessageId
                          ? { ...msg, toolBlocks: [...toolBlocks] }
                          : msg
                      )
                    );
                  }
                } else if (event.type === 'tool_result') {
                  // Find the corresponding tool block and add result
                  const toolIndex = toolBlocks.findIndex(tb => tb.tool.id === event.tool_result.tool_use_id);
                  if (toolIndex !== -1) {
                    toolBlocks[toolIndex].result = typeof event.tool_result.content === 'string'
                      ? event.tool_result.content
                      : JSON.stringify(event.tool_result.content, null, 2);

                    // Update message
                    setMessages((prev) =>
                      prev.map((msg) =>
                        msg.id === assistantMessageId
                          ? { ...msg, toolBlocks: [...toolBlocks] }
                          : msg
                      )
                    );
                  }
                } else if (event.type === 'message_complete') {
                  setCurrentSessionId(event.session_id);
                  // Reload sessions to show the new/updated session
                  await loadSessions();
                } else if (event.type === 'error') {
                  const errorMsg = typeof event.error === 'string' ? event.error : JSON.stringify(event.error);
                  throw new Error(errorMsg);
                }
              } catch (e) {
                console.error('Failed to parse SSE event:', e);
              }
            }
          }
        }
      }

      setIsLoading(false);
    } catch (error) {
      console.error('Failed to send message:', error);
      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === assistantMessageId
            ? { ...msg, content: 'Sorry, I encountered an error. Please try again.' }
            : msg
        )
      );
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleNewChat = () => {
    setMessages([]);
    setCurrentSessionId(null);
  };

  const handleLogout = () => {
    onSignOut();
  };

  const handleDeleteSession = async (sessionId: string) => {
    if (!window.confirm('Are you sure you want to delete this chat? This action cannot be undone.')) {
      return;
    }

    try {
      const client = createApiClient();
      await client.delete(`/chat/session/${sessionId}`);

      // If the deleted session was the current one, clear the chat
      if (currentSessionId === sessionId) {
        setMessages([]);
        setCurrentSessionId(null);
      }

      // Reload sessions list
      await loadSessions();
    } catch (error) {
      console.error('Failed to delete session:', error);
      alert('Failed to delete chat. Please try again.');
    }
  };

  const handleStartEdit = (message: Message) => {
    if (message.role === 'user' && message.message_id) {
      setEditingMessageId(message.id);
      setEditContent(message.content);
    }
  };

  const handleCancelEdit = () => {
    setEditingMessageId(null);
    setEditContent('');
  };

  const handleSaveEdit = async (message: Message) => {
    if (!message.message_id || !currentSessionId || !editContent.trim()) return;

    try {
      const client = createApiClient();
      await client.put(`/chat/message/${message.message_id}`, {
        session_id: currentSessionId,
        new_content: editContent,
      });

      // Clear edit state
      setEditingMessageId(null);
      const editedContent = editContent;
      setEditContent('');

      // Reload session history to get the updated state (messages after this will be gone)
      await loadSessionHistory(currentSessionId);

      // Automatically send the edited message to Claude
      setIsLoading(true);

      // Create placeholder for assistant message
      const assistantMessageId = Math.random().toString();
      const assistantMessage: Message = {
        id: assistantMessageId,
        role: 'assistant',
        content: '',
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, assistantMessage]);

      const idToken = await getFreshIdToken();
      if (!idToken) {
        throw new Error('Not authenticated');
      }

      const API_URL = import.meta.env.VITE_API_URL;

      // Use fetch for streaming
      const response = await fetch(`${API_URL}/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`,
        },
        body: JSON.stringify({
          message: editedContent,
          session_id: currentSessionId,
          stream: true,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Stream the response in real-time
      const reader = response.body?.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let fullContent = '';
      const toolBlocks: ToolBlock[] = [];

      if (reader) {
        while (true) {
          const { done, value} = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() || '';

          for (const line of lines) {
            if (line.startsWith('data: ')) {
              const data = line.slice(6);
              try {
                const event = JSON.parse(data);

                if (event.type === 'content_block_delta' && event.delta?.text) {
                  fullContent += event.delta.text;
                  // Update the assistant message in real-time
                  setMessages((prev) =>
                    prev.map((msg) =>
                      msg.id === assistantMessageId
                        ? { ...msg, content: fullContent, toolBlocks: [...toolBlocks] }
                        : msg
                    )
                  );
                } else if (event.type === 'tool_use_start') {
                  const toolBlock: ToolBlock = {
                    type: 'tool_use',
                    tool: {
                      id: event.tool_use.id,
                      name: event.tool_use.name,
                      input: event.tool_use.input, // May be undefined initially
                    },
                    timestamp: new Date(),
                  };
                  toolBlocks.push(toolBlock);
                  setMessages((prev) =>
                    prev.map((msg) =>
                      msg.id === assistantMessageId
                        ? { ...msg, toolBlocks: [...toolBlocks] }
                        : msg
                    )
                  );
                } else if (event.type === 'tool_use_complete') {
                  const toolIndex = toolBlocks.findIndex(tb => tb.tool.id === event.tool_use.id);
                  if (toolIndex !== -1) {
                    toolBlocks[toolIndex].tool.input = event.tool_use.input;
                    setMessages((prev) =>
                      prev.map((msg) =>
                        msg.id === assistantMessageId
                          ? { ...msg, toolBlocks: [...toolBlocks] }
                          : msg
                      )
                    );
                  }
                } else if (event.type === 'tool_result') {
                  const toolIndex = toolBlocks.findIndex(tb => tb.tool.id === event.tool_result.tool_use_id);
                  if (toolIndex !== -1) {
                    toolBlocks[toolIndex].result = typeof event.tool_result.content === 'string'
                      ? event.tool_result.content
                      : JSON.stringify(event.tool_result.content, null, 2);
                    setMessages((prev) =>
                      prev.map((msg) =>
                        msg.id === assistantMessageId
                          ? { ...msg, toolBlocks: [...toolBlocks] }
                          : msg
                      )
                    );
                  }
                } else if (event.type === 'message_complete') {
                  // Reload sessions to show the updated session
                  await loadSessions();
                } else if (event.type === 'error') {
                  const errorMsg = typeof event.error === 'string' ? event.error : JSON.stringify(event.error);
                  throw new Error(errorMsg);
                }
              } catch (e) {
                console.error('Failed to parse SSE event:', e);
              }
            }
          }
        }
      }

      setIsLoading(false);
    } catch (error) {
      console.error('Failed to edit message or get response:', error);
      setMessages((prev) =>
        prev.filter((msg) => msg.role !== 'assistant' || msg.content !== '')
      );
      setIsLoading(false);
      alert('Failed to edit message. Please try again.');
    }
  };

  return (
    <div className="flex h-screen overflow-hidden">
      {/* Sidebar */}
      {showSidebar && (
        <div className="sidebar">
          <div className="p-4 border-b border-gray-200">
            <div className="flex flex-col items-center mb-4">
              <img
                src="https://app.asai.alphastreet.io/assets/images/logo.svg"
                alt="ASAI Logo"
                className="w-20 h-auto mb-2"
              />
              <h2 className="text-lg font-bold text-gray-900 text-center">ASAI Chat</h2>
            </div>
            <button
              onClick={handleNewChat}
              className="w-full btn-primary text-sm"
            >
              + New Chat
            </button>
          </div>

          {/* Chat Sessions */}
          <div className="flex-1 overflow-y-auto p-4">
            <h3 className="text-xs font-semibold text-gray-500 uppercase mb-2">
              Recent Chats
            </h3>
            {sessions.map((session) => (
              <div
                key={session.session_id}
                className={`group relative flex items-center rounded-lg mb-2 transition-colors ${
                  currentSessionId === session.session_id
                    ? 'bg-asai-primary text-white'
                    : 'bg-white hover:bg-gray-100 text-gray-900'
                }`}
              >
                <button
                  onClick={() => loadSessionHistory(session.session_id)}
                  className="flex-1 text-left p-3 min-w-0 overflow-hidden"
                  title={session.title || `Chat ${session.session_id.slice(-8)}`}
                >
                  <div className="text-sm font-medium truncate">
                    {session.title || `Chat ${session.session_id.slice(-8)}`}
                  </div>
                  <div className="text-xs opacity-75">
                    {session.message_count} messages
                  </div>
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteSession(session.session_id);
                  }}
                  className={`p-2 mr-2 rounded opacity-0 group-hover:opacity-100 transition-opacity ${
                    currentSessionId === session.session_id
                      ? 'hover:bg-white/20 text-white'
                      : 'hover:bg-red-50 text-red-600'
                  }`}
                  title="Delete chat"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              </div>
            ))}
          </div>

          {/* User Info */}
          <div className="p-4 border-t border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">
                  {userInfo?.name || userInfo?.email || 'User'}
                </p>
                <p className="text-xs text-gray-500 truncate">
                  {userInfo?.email}
                </p>
              </div>
              <button
                onClick={handleLogout}
                className="ml-2 text-sm text-gray-600 hover:text-red-600"
                title="Logout"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Main Chat Area */}
      <div className="flex-1 flex flex-col">
        {/* Header */}
        <div className="bg-white border-b border-gray-200 p-4 flex items-center justify-between">
          <button
            onClick={() => setShowSidebar(!showSidebar)}
            className="mr-4 text-gray-600 hover:text-gray-900"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
          <h1 className="text-xl font-semibold text-gray-900">
            ASAI Financial Assistant
          </h1>
          <div className="w-6"></div>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-6 bg-gray-50">
          {messages.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center px-4">
              <img
                src="https://app.asai.alphastreet.io/assets/images/logo.svg"
                alt="ASAI Logo"
                className="w-48 h-auto mb-6 opacity-50"
              />
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                Welcome to ASAI
              </h2>
              <p className="text-gray-600 max-w-md mb-8">
                Your intelligent financial assistant powered by AI
              </p>

              {/* Sample Query Suggestions */}
              <div className="w-full max-w-3xl">
                <p className="text-sm font-medium text-gray-700 mb-4">Try asking:</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <button
                    onClick={() => setInputMessage('Show me earnings calls happening today')}
                    className="p-4 bg-white border-2 border-gray-200 rounded-lg text-left hover:border-asai-primary hover:shadow-md transition-all group"
                  >
                    <div className="flex items-start">
                      <svg className="w-5 h-5 text-asai-primary mt-0.5 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      <div>
                        <p className="text-sm font-medium text-gray-900 group-hover:text-asai-primary">Earnings calls today</p>
                        <p className="text-xs text-gray-500 mt-1">View live and upcoming calls</p>
                      </div>
                    </div>
                  </button>

                  <button
                    onClick={() => setInputMessage('Analyze the live transcript for Tesla earnings call')}
                    className="p-4 bg-white border-2 border-gray-200 rounded-lg text-left hover:border-asai-primary hover:shadow-md transition-all group"
                  >
                    <div className="flex items-start">
                      <svg className="w-5 h-5 text-asai-primary mt-0.5 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                      </svg>
                      <div>
                        <p className="text-sm font-medium text-gray-900 group-hover:text-asai-primary">Analyze live transcript</p>
                        <p className="text-xs text-gray-500 mt-1">Real-time earnings call insights</p>
                      </div>
                    </div>
                  </button>

                  <button
                    onClick={() => setInputMessage('Compare Q4 revenue and EPS for Apple, Microsoft, and Google')}
                    className="p-4 bg-white border-2 border-gray-200 rounded-lg text-left hover:border-asai-primary hover:shadow-md transition-all group"
                  >
                    <div className="flex items-start">
                      <svg className="w-5 h-5 text-asai-primary mt-0.5 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                      </svg>
                      <div>
                        <p className="text-sm font-medium text-gray-900 group-hover:text-asai-primary">Compare financials</p>
                        <p className="text-xs text-gray-500 mt-1">Side-by-side company metrics</p>
                      </div>
                    </div>
                  </button>

                  <button
                    onClick={() => setInputMessage('What are the key highlights from Apple\'s latest earnings call?')}
                    className="p-4 bg-white border-2 border-gray-200 rounded-lg text-left hover:border-asai-primary hover:shadow-md transition-all group"
                  >
                    <div className="flex items-start">
                      <svg className="w-5 h-5 text-asai-primary mt-0.5 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
                      </svg>
                      <div>
                        <p className="text-sm font-medium text-gray-900 group-hover:text-asai-primary">Earnings highlights</p>
                        <p className="text-xs text-gray-500 mt-1">Key takeaways from calls</p>
                      </div>
                    </div>
                  </button>

                  <button
                    onClick={() => setInputMessage('Show me recent SEC filings for NVIDIA')}
                    className="p-4 bg-white border-2 border-gray-200 rounded-lg text-left hover:border-asai-primary hover:shadow-md transition-all group"
                  >
                    <div className="flex items-start">
                      <svg className="w-5 h-5 text-asai-primary mt-0.5 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      <div>
                        <p className="text-sm font-medium text-gray-900 group-hover:text-asai-primary">SEC filings</p>
                        <p className="text-xs text-gray-500 mt-1">10-K, 10-Q, 8-K documents</p>
                      </div>
                    </div>
                  </button>

                  <button
                    onClick={() => setInputMessage('What did the CEO say about AI strategy in recent earnings calls?')}
                    className="p-4 bg-white border-2 border-gray-200 rounded-lg text-left hover:border-asai-primary hover:shadow-md transition-all group"
                  >
                    <div className="flex items-start">
                      <svg className="w-5 h-5 text-asai-primary mt-0.5 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                      <div>
                        <p className="text-sm font-medium text-gray-900 group-hover:text-asai-primary">Search transcripts</p>
                        <p className="text-xs text-gray-500 mt-1">Find specific topics discussed</p>
                      </div>
                    </div>
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="max-w-4xl mx-auto">
              {messages.map((message) => (
                <div
                  key={message.id}
                  className={`chat-message ${
                    message.role === 'user'
                      ? 'chat-message-user'
                      : 'chat-message-assistant'
                  }`}
                >
                  {editingMessageId === message.id ? (
                    <div className="space-y-2">
                      <textarea
                        value={editContent}
                        onChange={(e) => setEditContent(e.target.value)}
                        className="w-full p-3 border-2 border-asai-primary rounded-md resize-none text-gray-900 bg-white focus:outline-none focus:ring-2 focus:ring-asai-primary"
                        rows={4}
                        autoFocus
                      />
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleSaveEdit(message)}
                          className="px-4 py-2 bg-asai-primary text-white rounded-md text-sm font-medium hover:opacity-90 transition-all"
                        >
                          Save & Resend
                        </button>
                        <button
                          onClick={handleCancelEdit}
                          className="px-4 py-2 border-2 border-asai-primary text-asai-primary rounded-md text-sm font-medium hover:bg-asai-primary hover:text-white transition-all"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      {/* Tool Call Visualization */}
                      {message.toolBlocks && message.toolBlocks.length > 0 && (
                        <ToolCallVisualization toolBlocks={message.toolBlocks} isAdmin={isAdmin} />
                      )}

                      {/* Message Content */}
                      <div className="markdown-content">
                        <ReactMarkdown
                          remarkPlugins={[remarkGfm]}
                          components={{
                            a: ({ node, ...props }) => (
                              <a {...props} target="_blank" rel="noopener noreferrer" />
                            ),
                            code: ({ node, className, children, ...props }: any) => {
                              const match = /language-(\w+)/.exec(className || '');
                              const language = match ? match[1] : '';
                              const inline = props.inline;

                              // Render charts from JSON code blocks
                              if (!inline && language === 'json') {
                                const content = String(children).replace(/\n$/, '');
                                // Check if this looks like chart data
                                try {
                                  const parsed = JSON.parse(content);
                                  if (parsed.type && parsed.data && ['line', 'bar', 'pie', 'area'].includes(parsed.type)) {
                                    return <ChartRenderer chartJson={content} />;
                                  }
                                } catch {
                                  // Not a chart, fall through to regular code block
                                }
                              }

                              // Regular code block
                              return (
                                <code className={className} {...props}>
                                  {children}
                                </code>
                              );
                            },
                          }}
                        >
                          {message.content}
                        </ReactMarkdown>
                      </div>
                      <div className="flex items-center justify-between mt-2">
                        <div className="text-xs opacity-70">
                          {message.timestamp.toLocaleTimeString()}
                          {message.edited && (
                            <span className="ml-2 italic text-asai-primary font-medium">(edited)</span>
                          )}
                        </div>
                        {message.role === 'user' && message.message_id && (
                          <button
                            onClick={() => handleStartEdit(message)}
                            className="flex items-center space-x-1 px-3 py-1.5 text-sm font-medium text-white bg-asai-primary hover:opacity-90 rounded-md shadow-sm hover:shadow-md transition-all"
                            title="Edit message"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                            <span>Edit</span>
                          </button>
                        )}
                      </div>
                    </>
                  )}
                </div>
              ))}
              {isLoading && (
                <div className="chat-message chat-message-assistant">
                  <LoadingDots />
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>
          )}
        </div>

        {/* Input Area */}
        <div className="bg-white border-t border-gray-200 p-4">
          <div className="max-w-4xl mx-auto flex items-end space-x-2">
            <textarea
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              onKeyDown={handleKeyPress}
              placeholder="Ask me anything about financial data..."
              className="input-field resize-none"
              rows={3}
              disabled={isLoading}
            />
            <button
              onClick={handleSendMessage}
              disabled={!inputMessage.trim() || isLoading}
              className="btn-primary px-6 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
